import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, FileText, X, UserX, Settings2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { api, fileUrl } from "../lib/api";

export const ExtraFiles = ({ e, canEdit, onDone }) => {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const r = await api.uploadFile(file); await api.addSaudiExtra(e.id, { label: label.trim(), file_id: r.attachment_id, file_name: r.attachment_name }); toast.success("تمت إضافة الملف"); setLabel(""); onDone(); }
    catch (er) { toast.error(er?.response?.data?.detail || "فشل الرفع"); } finally { setBusy(false); }
  };
  const items = e.saudi_extra || [];
  return (
    <div className="space-y-2" data-testid={`saudi-extra-${e.id}`}>
      {items.length > 0 && <p className="text-xs font-bold text-slate-500">ملفات إضافية</p>}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((x) => (
          <div key={x.id} data-testid={`saudi-extra-item-${x.id}`} className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-2.5 py-2 text-xs">
            <FileText className="h-4 w-4 text-blue-600" /><span className="flex-1 truncate font-semibold text-slate-700">{x.label}</span>
            <a href={fileUrl(x.file_id)} target="_blank" rel="noreferrer" className="text-blue-700 underline">عرض</a>
            {canEdit && <button data-testid={`saudi-extra-del-${x.id}`} onClick={async () => { await api.delSaudiExtra(e.id, x.id); onDone(); }} className="rounded p-1 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex gap-2">
          <Input data-testid={`saudi-extra-label-${e.id}`} value={label} onChange={(ev) => setLabel(ev.target.value)} placeholder="اسم الملف (مثال: خطاب تعريف)" className="h-9 text-sm" />
          <input ref={ref} type="file" hidden data-testid={`saudi-extra-input-${e.id}`} onChange={(ev) => { up(ev.target.files?.[0]); ev.target.value = ""; }} />
          <Button size="sm" data-testid={`saudi-extra-add-${e.id}`} disabled={busy || !label.trim()} onClick={() => ref.current?.click()} className="h-9 shrink-0 gap-1">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} إضافة ملف</Button>
        </div>
      )}
    </div>
  );
};

export const DeleteEmp = ({ e, onDone }) => {
  const [open, setOpen] = useState(false);
  const del = async () => { try { await api.deleteEmployee(e.id); toast.success(`تم حذف ${e.name}`); onDone(); } catch (er) { toast.error(er?.response?.data?.detail || "تعذّر الحذف"); } };
  return (
    <>
      <Button size="sm" variant="outline" data-testid={`saudi-delete-${e.id}`} onClick={() => setOpen(true)} className="gap-1 text-red-600 hover:bg-red-50"><UserX className="h-4 w-4" /> حذف الموظف</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader className="text-right"><AlertDialogTitle>حذف {e.name}؟</AlertDialogTitle>
            <AlertDialogDescription>سيُحذف الموظف نهائياً من الموقع مع بياناته وملفاته. لا يمكن التراجع.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter className="gap-2"><AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid={`saudi-delete-confirm-${e.id}`} onClick={del} className="bg-red-600 hover:bg-red-700">حذف نهائي</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const SlotManager = ({ slots, custom, onDone }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const add = async () => { try { await api.addSaudiSlot({ label: label.trim() }); setLabel(""); toast.success("تمت إضافة الخانة لكل الموظفين السعوديين"); onDone(); } catch (er) { toast.error(er?.response?.data?.detail || "تعذّر الإضافة"); } };
  return (
    <div className="space-y-2">
      <Button size="sm" variant="outline" data-testid="saudi-slots-manage" onClick={() => setOpen((v) => !v)} className="gap-1.5"><Settings2 className="h-4 w-4" /> خانات الملفات</Button>
      {open && (
        <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="saudi-slots-panel">
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(slots).map(([k, l]) => (
              <span key={k} className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs">{l}
                {custom.includes(k) && <button data-testid={`saudi-slot-remove-${k}`} onClick={async () => { await api.removeSaudiSlot(k); onDone(); }} className="text-red-500"><X className="h-3 w-3" /></button>}</span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input data-testid="saudi-slot-new-label" value={label} onChange={(ev) => setLabel(ev.target.value)} placeholder="اسم خانة جديدة لكل الموظفين (مثال: شهادة الخبرة)" className="h-9 bg-white text-sm" />
            <Button size="sm" data-testid="saudi-slot-add" disabled={!label.trim()} onClick={add} className="h-9 shrink-0 gap-1"><Plus className="h-4 w-4" /> إضافة خانة</Button>
          </div>
        </div>
      )}
    </div>
  );
};
